import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../lib/api'
import { color, radius, shadow } from '../theme/tokens'
import type { Environment, PolicyEnvelope, Scenario } from '../lib/types'
import { Header, SideNav } from '../components/Shell'
import { InspectorPanel } from '../components/policy/InspectorPanel'
import {
  ChannelsSection,
  ComplianceSection,
  DataSection,
  EscalationSection,
  GuardrailsSection,
  MatrixSection,
  PersonaSection,
  PersonasSection,
  ToolsSection,
  type SectionProps,
} from '../components/policy/sections'
import { Button } from '../components/ui'

const SECTIONS = [
  { n: 1, title: 'Persona' },
  { n: 2, title: 'Contexto e dados' },
  { n: 3, title: 'Política de negociação' },
  { n: 4, title: 'Guardrails' },
  { n: 5, title: 'Escalonamento' },
  { n: 6, title: 'Ferramentas' },
  { n: 7, title: 'Canais e cadência' },
  { n: 8, title: 'Compliance e LGPD' },
  { n: 9, title: 'Personas de teste' },
]

export function ConfiguracaoPolitica() {
  const [envelope, setEnvelope] = useState<PolicyEnvelope | null>(null)
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [section, setSection] = useState(3)
  const [environment, setEnvironment] = useState<Environment>('draft')
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const saveTimer = useRef<number | null>(null)

  useEffect(() => {
    Promise.all([api.getDraft(), api.listScenarios()])
      .then(([draft, scenarioList]) => {
        setEnvelope(draft)
        setScenarios(scenarioList)
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Falha ao carregar a política'),
      )
  }, [])

  /**
   * Aplica uma mutação local e persiste com debounce.
   *
   * O estado local responde na hora (a tela não pode engasgar a cada tecla) e o
   * servidor recompila prompt, validação e prévia de impacto — que voltam como
   * fonte da verdade.
   */
  const patch = useCallback((mutate: (draft: PolicyEnvelope['policy']) => void) => {
    setEnvelope((current) => {
      if (!current) return current
      const next = structuredClone(current.policy)
      mutate(next)
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      saveTimer.current = window.setTimeout(() => {
        setSaving(true)
        api
          .saveDraft(next)
          .then(setEnvelope)
          .catch((err: unknown) =>
            setError(err instanceof Error ? err.message : 'Falha ao salvar o rascunho'),
          )
          .finally(() => setSaving(false))
      }, 400)
      return { ...current, policy: next }
    })
  }, [])

  const sectionProps: SectionProps | null = useMemo(
    () =>
      envelope
        ? {
            policy: envelope.policy,
            patch,
            impact: envelope.impact,
            answerRate: envelope.answer_rate,
          }
        : null,
    [envelope, patch],
  )

  if (error && !envelope) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          textAlign: 'center',
          padding: 40,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: color.redInk }}>{error}</div>
        <div
          style={{ fontSize: 13, color: color.inkSubtle, fontFamily: "'Roboto Mono', monospace" }}
        >
          uvicorn app.main:app --reload --port 8000
        </div>
      </div>
    )
  }

  if (!envelope || !sectionProps) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color.inkSubtle,
        }}
      >
        Carregando a política…
      </div>
    )
  }

  const { policy, validation, diff_count: diffCount, published_version: publishedVersion } = envelope

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Header
        subtitle="Construtor de política"
        badge="back-office · o banco não vê esta tela"
      >
        <Link
          to="/"
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: color.brandLight,
            border: '1px solid rgba(240,134,47,.4)',
            borderRadius: radius.md,
            padding: '7px 13px',
          }}
        >
          Abrir demonstração ao vivo →
        </Link>
      </Header>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <SideNav />

        <aside
          style={{
            width: 250,
            flex: 'none',
            background: color.surface,
            borderRight: `1px solid ${color.border}`,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div style={{ padding: '16px 16px 10px', flex: 'none' }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.09em',
                color: color.inkSubtle,
              }}
            >
              POLÍTICA DE NEGOCIAÇÃO
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>
              Cobrança Varejo · Banco Vantagem
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0 10px 10px' }}>
            {SECTIONS.map((item) => {
              const active = section === item.n
              return (
                <button
                  key={item.n}
                  type="button"
                  onClick={() => setSection(item.n)}
                  aria-current={active ? 'true' : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '10px 11px',
                    borderRadius: 9,
                    cursor: 'pointer',
                    marginBottom: 2,
                    transition: 'background .15s ease',
                    background: active ? color.brandTint : 'transparent',
                    borderLeft: `3px solid ${active ? color.brand : 'transparent'}`,
                    border: 'none',
                    borderLeftStyle: 'solid',
                    borderLeftWidth: 3,
                    borderLeftColor: active ? color.brand : 'transparent',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: color.inkFaint,
                      fontVariantNumeric: 'tabular-nums',
                      width: 12,
                      flex: 'none',
                    }}
                  >
                    {item.n}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      color: active ? color.ink : color.inkMuted,
                    }}
                  >
                    {item.title}
                  </span>
                </button>
              )
            })}
          </div>

          <div
            style={{
              flex: 'none',
              borderTop: `1px solid ${color.border}`,
              padding: '12px 16px',
              background: color.surfaceMuted,
            }}
          >
            <div style={{ fontSize: 11, color: color.inkSubtle }}>Versão de trabalho</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 4 }}>
              <span
                style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
              >
                v{policy.version}
              </span>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: radius.pill,
                  background: color.amberTint,
                  color: color.amberInk,
                }}
              >
                RASCUNHO
              </span>
            </div>
            <div style={{ fontSize: 11, color: color.inkFaint, marginTop: 5 }}>
              {saving ? 'salvando…' : `alterada por ${policy.updated_by}`}
            </div>
          </div>
        </aside>

        <main style={{ flex: 1, minWidth: 0, display: 'flex', minHeight: 0 }}>
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '20px 24px 84px' }}>
            {section === 1 ? <PersonaSection {...sectionProps} /> : null}
            {section === 2 ? <DataSection {...sectionProps} /> : null}
            {section === 3 ? <MatrixSection {...sectionProps} /> : null}
            {section === 4 ? <GuardrailsSection {...sectionProps} /> : null}
            {section === 5 ? <EscalationSection {...sectionProps} /> : null}
            {section === 6 ? <ToolsSection {...sectionProps} /> : null}
            {section === 7 ? <ChannelsSection {...sectionProps} /> : null}
            {section === 8 ? <ComplianceSection {...sectionProps} /> : null}
            {section === 9 ? <PersonasSection scenarios={scenarios} /> : null}
          </div>

          <InspectorPanel prompt={envelope.prompt} validation={validation} />
        </main>
      </div>

      <footer
        style={{
          position: 'absolute',
          left: 60,
          right: 0,
          bottom: 0,
          height: 60,
          background: color.panel,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '0 20px',
          zIndex: 30,
          boxShadow: shadow.footer,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          v{policy.version}
        </span>
        <select
          value={environment}
          onChange={(event) => setEnvironment(event.target.value as Environment)}
          aria-label="Ambiente"
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: '7px 10px',
            borderRadius: radius.md,
            border: `1px solid ${color.panelBorder}`,
            background: color.panelRaised,
            color: '#fff',
          }}
        >
          <option value="draft">Rascunho</option>
          <option value="sandbox">Sandbox</option>
          <option value="prod">Produção</option>
        </select>
        <span style={{ fontSize: 11.5, color: color.panelTextMuted }}>
          alterada por {policy.updated_by} ·{' '}
          <b style={{ color: color.panelText }}>
            {diffCount} {diffCount === 1 ? 'alteração' : 'alterações'}
          </b>{' '}
          vs. v{publishedVersion} publicada
        </span>

        <div style={{ flex: 1 }} />

        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 11.5,
            fontWeight: 600,
            color: validation.errors ? color.redPanel : color.greenPanel,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: validation.errors ? color.redLive : color.green,
            }}
          />
          {validation.summary}
        </span>

        <Button variant="dark" onClick={() => setToast('Validação executada — veja o painel direito.')}>
          Validar
        </Button>
        <Button
          variant="dark"
          onClick={() => {
            setEnvironment('sandbox')
            setToast('Rascunho publicado em sandbox. Rode um cenário na demonstração.')
          }}
        >
          Testar em sandbox
        </Button>
        <Button
          variant="primary"
          disabled={!validation.can_publish}
          onClick={() => setModalOpen(true)}
          style={{ padding: '11px 20px', fontSize: 13, fontWeight: 700 }}
          title={validation.can_publish ? undefined : 'Corrija os erros antes de publicar'}
        >
          Publicar
        </Button>
      </footer>

      {toast ? (
        <div
          role="status"
          onAnimationEnd={() => setToast(null)}
          style={{
            position: 'absolute',
            bottom: 76,
            right: 24,
            zIndex: 50,
            background: color.panel,
            color: '#fff',
            borderRadius: radius.lg,
            padding: '11px 16px',
            fontSize: 12.5,
            boxShadow: shadow.modal,
            animation: 'mat .3s ease both',
          }}
        >
          {toast}
          <button
            type="button"
            onClick={() => setToast(null)}
            aria-label="Fechar aviso"
            style={{
              marginLeft: 12,
              background: 'none',
              border: 'none',
              color: color.panelTextMuted,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      ) : null}

      {modalOpen ? (
        <PublishModal
          envelope={envelope}
          onClose={() => setModalOpen(false)}
          onPublished={(message) => {
            setModalOpen(false)
            setToast(message)
            void api.getDraft().then(setEnvelope)
          }}
        />
      ) : null}
    </div>
  )
}

function PublishModal({
  envelope,
  onClose,
  onPublished,
}: {
  envelope: PolicyEnvelope
  onClose(): void
  onPublished(message: string): void
}) {
  const [justification, setJustification] = useState('')
  const [approver, setApprover] = useState('Diretoria de Crédito e Cobrança')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const applied = envelope.policy.matrix.find((row) => row.applied) ?? envelope.policy.matrix[0]

  const publish = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.publish(justification, approver)
      onPublished(
        `Política v${result.version.version} publicada em produção. Nova versão de trabalho: v${result.next_draft_version}.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao publicar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Publicar política"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(24,30,42,.55)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        style={{
          width: 920,
          maxWidth: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          background: color.surface,
          borderRadius: radius.panel,
          boxShadow: shadow.modal,
          animation: 'mat .26s ease both',
        }}
      >
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${color.border}` }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.09em',
              color: color.inkSubtle,
            }}
          >
            PUBLICAR POLÍTICA
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: '4px 0 0' }}>
            v{envelope.policy.version} → produção
          </h2>
        </div>

        <div
          style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
        >
          <div>
            <div
              style={{ fontSize: 11, fontWeight: 700, color: color.inkSubtle, marginBottom: 8 }}
            >
              v{envelope.published_version} · PUBLICADA
            </div>
            <DiffBlock
              tone="old"
              lines={[
                ['linha aplicada', `${applied.segment} · ${applied.bucket}`],
                ['política em produção', `v${envelope.published_version}`],
                ['alterações pendentes', String(envelope.diff_count)],
              ]}
            />
          </div>
          <div>
            <div
              style={{ fontSize: 11, fontWeight: 700, color: color.greenInk, marginBottom: 8 }}
            >
              v{envelope.policy.version} · RASCUNHO
            </div>
            <DiffBlock
              tone="new"
              lines={[
                ['teto_a_vista', `${applied.cash_cap}%`],
                ['teto_entrada', `${applied.installment_cap}%`],
                ['max_parcelas', String(applied.max_installments)],
                ['abertura', envelope.policy.negotiation.opening],
                ['regra_de_ouro', envelope.policy.negotiation.golden_rule ? 'on' : 'off'],
              ]}
            />
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: 'span 2' }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Justificativa da alteração</span>
            <textarea
              rows={2}
              value={justification}
              onChange={(event) => setJustification(event.target.value)}
              placeholder="Ex.: ampliação de teto para segmento Premium aprovada no comitê de crédito de 22/07."
              style={{
                padding: '10px 12px',
                border: `1px solid ${color.border}`,
                borderRadius: radius.md,
                fontSize: 12.5,
                lineHeight: 1.5,
                resize: 'vertical',
              }}
            />
            <span style={{ fontSize: 11, color: color.inkFaint }}>
              Mínimo de 10 caracteres — entra na trilha de auditoria junto com a versão publicada.
            </span>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Aprovador</span>
            <select
              value={approver}
              onChange={(event) => setApprover(event.target.value)}
              style={{
                padding: '10px 12px',
                border: `1px solid ${color.border}`,
                borderRadius: radius.md,
                fontSize: 12.5,
                background: color.surface,
              }}
            >
              <option>Diretoria de Crédito e Cobrança</option>
              <option>Comitê de Risco</option>
              <option>Compliance</option>
            </select>
          </label>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              fontSize: 11.5,
              color: color.inkSubtle,
              lineHeight: 1.5,
            }}
          >
            A v{envelope.published_version} permanece disponível para rollback em um clique por 90
            dias.
          </div>
        </div>

        {error ? (
          <div
            style={{
              margin: '0 24px',
              padding: '10px 14px',
              background: color.redTint,
              border: `1px solid ${color.redBorder}`,
              color: color.redDeep,
              borderRadius: radius.md,
              fontSize: 12.5,
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${color.border}`,
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            onClick={() => void publish()}
            disabled={busy || justification.trim().length < 10}
            style={{ padding: '11px 22px', fontSize: 13, fontWeight: 700 }}
          >
            {busy ? 'Publicando…' : 'Publicar em produção'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function DiffBlock({ tone, lines }: { tone: 'old' | 'new'; lines: [string, string][] }) {
  const isNew = tone === 'new'
  return (
    <div
      style={{
        fontFamily: "'Roboto Mono', monospace",
        fontSize: 11,
        lineHeight: 1.7,
        border: `1px solid ${isNew ? color.greenBorder : color.border}`,
        borderRadius: radius.lg,
        padding: 12,
        background: isNew ? color.greenTintSoft : color.surfaceMuted,
      }}
    >
      {lines.map(([key, value]) => (
        <div key={key}>
          {key} ={' '}
          <span style={{ color: isNew ? color.greenInk : color.red, fontWeight: 500 }}>{value}</span>
        </div>
      ))}
    </div>
  )
}
